import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkAdminExists, initializeFirstAdmin } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import './AdminLogin.css';

function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [adminExists, setAdminExists] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  useEffect(() => {
    async function checkForAdmin() {
      try {
        const exists = await checkAdminExists();
        setAdminExists(exists);
      } catch (err) {
        console.error('Failed to check admin status:', err);
        setAdminExists(true);
      } finally {
        setChecking(false);
      }
    }
    checkForAdmin();
  }, []);

  async function handleLogin(e) {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);

      await login({ email, password }, true);
      
      // Redirect to the page they tried to visit or dashboard
      const from = location.state?.from?.pathname || '/admin/dashboard';
      navigate(from, { replace: true });
    } catch (error) {
      setError('Failed to sign in: ' + error.message);
    }
    setLoading(false);
  }

  function getPasswordStrength(pwd) {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^a-zA-Z0-9]/.test(pwd)) score++;

    if (score <= 1) return { label: 'Weak', color: '#dc2626', percent: 20 };
    if (score === 2) return { label: 'Fair', color: '#f59e0b', percent: 40 };
    if (score === 3) return { label: 'Good', color: '#eab308', percent: 60 };
    if (score === 4) return { label: 'Strong', color: '#22c55e', percent: 80 };
    return { label: 'Very Strong', color: '#16a34a', percent: 100 };
  }

  const passwordStrength = getPasswordStrength(password);

  async function handleSetup(e) {
    e.preventDefault();

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!/[A-Z]/.test(password)) {
      setError('Password must contain at least one uppercase letter');
      return;
    }

    if (!/[a-z]/.test(password)) {
      setError('Password must contain at least one lowercase letter');
      return;
    }

    if (!/\d/.test(password)) {
      setError('Password must contain at least one number');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setError('');
      setLoading(true);

      await initializeFirstAdmin(email, password, name);
      await login({ email, password }, true);
      
      navigate('/admin/dashboard', { replace: true });
    } catch (error) {
      setError('Failed to create admin account: ' + error.message);
    }
    setLoading(false);
  }

  if (checking) {
    return (
      <div className="admin-page">
        <div className="admin-login-container">
          <div className="admin-login-box">
            <p style={{ textAlign: 'center', color: '#666' }}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-login-container">
        <div className="admin-login-box">
          {adminExists ? (
            <>
              <div className="admin-login-header">
                <h2>Admin Login</h2>
                <p>Please sign in to access the admin dashboard</p>
              </div>
              
              {error && <div className="admin-login-error">{error}</div>}
              
              <form onSubmit={handleLogin} className="admin-login-form">
                <div className="form-group">
                  <input
                    type="email"
                    required
                    className="admin-input"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <input
                    type="password"
                    required
                    className="admin-input"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="admin-submit-button"
                  disabled={loading}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="admin-login-header">
                <h2>Admin Setup</h2>
                <p>No admin account found. Create the first admin account to get started.</p>
              </div>
              
              {error && <div className="admin-login-error">{error}</div>}
              
              <form onSubmit={handleSetup} className="admin-login-form">
                <div className="form-group">
                  <input
                    type="text"
                    required
                    className="admin-input"
                    placeholder="Full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <input
                    type="email"
                    required
                    className="admin-input"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <input
                    type="password"
                    required
                    className="admin-input"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    minLength={8}
                  />
                </div>

                <div className="form-group">
                  <input
                    type="password"
                    required
                    className="admin-input"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                  />
                  {confirmPassword.length > 0 && password !== confirmPassword && (
                    <span className="password-mismatch">Passwords do not match</span>
                  )}
                  {confirmPassword.length > 0 && password === confirmPassword && (
                    <span className="password-match">Passwords match</span>
                  )}
                </div>

                {password.length > 0 && (
                  <div className="password-strength">
                    <div className="strength-bar">
                      <div
                        className="strength-fill"
                        style={{ width: `${passwordStrength.percent}%`, backgroundColor: passwordStrength.color }}
                      />
                    </div>
                    <span className="strength-label" style={{ color: passwordStrength.color }}>
                      {passwordStrength.label}
                    </span>
                  </div>
                )}
                <ul className="password-rules">
                  <li className={password.length >= 8 ? 'met' : ''}>At least 8 characters</li>
                  <li className={/[A-Z]/.test(password) ? 'met' : ''}>One uppercase letter</li>
                  <li className={/[a-z]/.test(password) ? 'met' : ''}>One lowercase letter</li>
                  <li className={/\d/.test(password) ? 'met' : ''}>One number</li>
                  <li className={/[^a-zA-Z0-9]/.test(password) ? 'met' : ''}>One special character (recommended)</li>
                </ul>
                
                <button 
                  type="submit" 
                  className="admin-submit-button"
                  disabled={loading}
                >
                  {loading ? 'Creating account...' : 'Create Admin Account'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminLogin;
